<template>
    <ul class="m-0 list-none p-0">
        <!-- Keyed by uuid: the menu answers the delete confirmation it raised, so it must survive an earlier row leaving. -->
        <li
            v-for="selection in highlights"
            :key="selection.uuid"
            :data-note-uuid="selection.uuid"
            class="border-b border-theme-border last:border-b-0"
        >
            <!-- Editing happens in place: the row becomes the editor. -->
            <NoteEditor v-if="isEditing(selection)" :selection="selection" />
            <div v-else class="flex items-stretch gap-2.5 py-3">
                <div class="w-[3px] shrink-0 rounded-sm" :style="{ backgroundColor: selection.color }"></div>

                <div class="min-w-0 flex-1">
                    <svg-icon
                        v-if="ui.listening.opened && ui.listening.uuidToListen === selection.uuid"
                        class="mb-1"
                        icon="baseline-volume_up-24px"
                        width="16px"
                        height="16px"
                    />

                    <!-- What the reader wrote leads the row; a highlight with no note keeps the passage as its
                         headline. A rich note shows its formatting, clipped inside its markup past the preview
                         length; the editor is where the whole thing is read. -->
                    <RichTextContent
                        v-if="richNotePreview(selection)"
                        ref="richNotes"
                        :html="richNotePreview(selection)"
                        :labels="embedLabels"
                        :audio-hosts="audioEmbedHosts"
                        class="mb-2 text-sm leading-[1.45] text-theme-text"
                    />
                    <p
                        v-else-if="hasNote(selection)"
                        class="mb-2 whitespace-pre-line break-words text-sm leading-[1.45] text-theme-text"
                    >
                        {{ notePreview(selection) }}
                    </p>

                    <!-- The bar already says which color this was highlighted in, so the
                         passage carries no fill and reads as the quote it is. -->
                    <blockquote class="m-0 p-0">
                        <button
                            type="button"
                            :lang="issue.lang"
                            class="block w-full cursor-pointer border-none bg-transparent p-0 text-left text-xs italic leading-[1.45] text-theme-toolbar-icon hover:opacity-75 focus-visible:ring-2 focus-visible:ring-theme-accent focus-visible:ring-offset-2"
                            @click="maybeGoToPage(selection)"
                        >
                            {{ selection.totalText }}
                        </button>
                    </blockquote>

                    <p v-if="buildMeta(selection)" class="mt-1.5 text-[11px] text-theme-toolbar-icon opacity-70">
                        {{ buildMeta(selection) }}
                    </p>
                </div>

                <div class="flex shrink-0 items-center gap-1">
                    <span
                        v-if="sharedColorsBySlug.has(selection.colorSlug)"
                        v-tooltip.top="sharedAuthorLabel(selection)"
                        class="inline-flex text-theme-toolbar-icon opacity-70"
                    >
                        <svg-icon icon="shared-group-24px" width="16" height="16" />
                        <span class="sr-only">{{ sharedAuthorLabel(selection) }}</span>
                    </span>
                    <ActionMenu :selection="selection" />
                </div>
            </div>
        </li>
    </ul>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import ActionMenu from './ActionMenu.vue';
import NoteEditor from './NoteEditor.vue';
import RichTextContent from '../../Components/RichTextContent.vue';
import formatLocalDateTime from '../../../js/utils/formatLocalDateTime.js';
import { clipHtmlToPreviewWords, clipToPreviewWords } from '../../../js/utils/notePreviewText.js';

export default {
    components: {
        ActionMenu,
        NoteEditor,
        RichTextContent,
    },
    props: {
        highlights: {
            type: Array,
            default: () => [],
        },
    },
    computed: {
        ...mapState(['ui', 'lang', 'issue', 'localizations', 'sharedColors']),
        ...mapGetters(['isEpub', 'isPdf', 'onlyMobileVisible', 'richTextEmbedHosts']),
        /**
         * Maps shared color slugs to their color objects for O(1) lookup.
         * @returns {Map<string, Object>}
         */
        sharedColorsBySlug() {
            const colorMap = new Map();
            for (const color of this.sharedColors) {
                colorMap.set(color.slug, color);
            }
            return colorMap;
        },
        /**
         * Texts for the play button and the provider link of an embedded video.
         * @returns {Object|undefined}
         */
        embedLabels() {
            return this.localizations[this.lang]?.richText?.embed;
        },
        /**
         * Providers whose embeds are players with no picture, so the row can
         * size them as a strip rather than a 16:9 box.
         * @returns {string[]}
         */
        audioEmbedHosts() {
            return this.richTextEmbedHosts?.audio ?? [];
        },
    },
    watch: {
        /**
         * The panel is hidden, not unmounted, so a video played from a row goes
         * on sounding behind a reader who has closed the notebook and cannot
         * reach its controls. Closing the panel puts every player back behind
         * its button. Turning the page does not: the panel stays open and a
         * reader listening while they read is doing it on purpose.
         */
        'ui.notebook.opened'(opened) {
            if (opened) {
                return;
            }

            (this.$refs.richNotes ?? []).forEach(note => note.releaseEmbeds());
        },
    },
    methods: {
        ...mapActions(['goToPage', 'goToLocation', 'toggleNotebook']),
        /**
         * Names the owner of this row's shared highlighter. SvgIcon forces aria-hidden at
         * runtime, so this is the icon's only accessible name as well as its tooltip.
         * @param {Object} selection
         * @returns {string}
         */
        sharedAuthorLabel(selection) {
            const { sharedBy, sharedWithMe } = this.localizations[this.lang].sharing;
            const authorEmail = this.sharedColorsBySlug.get(selection.colorSlug)?.author?.email;
            return authorEmail ? `${sharedBy} ${authorEmail}` : sharedWithMe;
        },
        /**
         * Whether this row is the note currently open in the editor.
         * @param {Object} selection
         * @returns {boolean}
         */
        isEditing(selection) {
            return this.ui.noteEditor.opened && this.ui.noteEditor.uuid === selection.uuid;
        },
        /**
         * Whether the row leads with a note rather than with the passage.
         * @param {Object} selection
         * @returns {boolean}
         */
        hasNote(selection) {
            // Both fields: an embed-only rich note projects to an empty plain text.
            return Boolean(selection.annotationText || selection.annotationHtml);
        },
        /**
         * The row shows the note clipped; the editor is where the whole thing is read.
         * @param {Object} selection
         * @returns {string}
         */
        notePreview(selection) {
            return clipToPreviewWords(selection.annotationText);
        },
        /**
         * The stored markup of a rich note, clipped to the preview length with the
         * formatting of the kept part intact.
         * @param {Object} selection
         * @returns {string} Empty for plain notes
         */
        richNotePreview(selection) {
            return selection.annotationHtml ? clipHtmlToPreviewWords(selection.annotationHtml) : '';
        },
        /**
         * Builds the meta line for a selection: page (PDF only) and last-modified date, joined by " · ".
         * Empty string when neither is available — the v-if on the <p> uses this to skip rendering.
         * @param {Object} selection
         * @returns {string}
         */
        buildMeta(selection) {
            const parts = [];
            if (this.isPdf && selection.startPage) parts.push(`p. ${selection.startPage}`);
            if (selection.updatedAt) parts.push(formatLocalDateTime(selection.updatedAt, this.lang));
            return parts.join(' · ');
        },
        /**
         * Navigates to the page or location of the selected highlight.
         * Supports EPUB R2 (CSS selector locator), EPUB R1 (legacy), and PDF.
         * @param {Object} selection - The highlight/note selection object
         */
        maybeGoToPage(selection) {
            if (document.querySelector('.ActionMenu.isOpen')) {
                return;
            }

            if (this.isEpub) {
                this.goToLocation(this.buildEpubNavigationPayload(selection));
            } else {
                this.goToPage({
                    page: selection.startPage,
                    trigger: 'click',
                    source: 'notes',
                });
            }

            if (this.onlyMobileVisible) {
                this.toggleNotebook();
            }
        },
        /**
         * Builds the navigation payload for EPUB highlights.
         * R2 highlights pass epubR2Location with href, type, and locations.
         * R1 legacy highlights use chapter href + character offset for precise positioning.
         * @param {Object} selection - The highlight/note selection object
         * @returns {Object} Payload for goToLocation action
         */
        buildEpubNavigationPayload(selection) {
            const epubR2Location = selection.epubR2Location ?? selection.location;

            // R2 with full locations: use directly
            if (epubR2Location?.locations) {
                return { epubR2Location };
            }

            // R1 legacy: use chapter href + text for precise navigation
            return {
                epubLocation: selection.epubLocation,
                totalText: selection.totalText,
                trigger: 'click',
                source: 'notes',
            };
        },
    },
};
</script>
